import { useState } from "react";
import { api, type GalleryAsset, type ProofingResults } from "../../lib/api";

interface ProofingTabProps {
  proofing: ProofingResults | null;
  loading: boolean;
  error: string | null;
  assets: GalleryAsset[];
}

export function ProofingTab({ proofing, loading, error, assets }: ProofingTabProps) {
  const [copyState, setCopyState] = useState<"" | "names" | "csv">("");

  if (loading) return <p style={{ color: "var(--muted)" }}>Loading proofing data…</p>;
  if (error) return <div className="alert alert-error">{error}</div>;
  if (!proofing || proofing.results.length === 0) {
    return (
      <div className="card" style={{ textAlign: "center", padding: "2rem" }}>
        <p style={{ margin: 0, color: "var(--muted)" }}>
          No client reactions yet — share the gallery link with your client
        </p>
      </div>
    );
  }

  // Build a lookup from asset_id → asset using the assets list.
  const thumbByID: Record<string, string> = {};
  const filenameByID: Record<string, string> = {};
  for (const a of assets) {
    thumbByID[a.id] = a.thumb_url;
    filenameByID[a.id] = a.filename;
  }

  const favourited = proofing.results.filter((r) => r.favourites > 0);
  const flagged = proofing.results.filter((r) => r.flags > 0);
  const commented = proofing.results.filter((r) => r.comments.length > 0);

  async function copyToClipboard(text: string, kind: "names" | "csv") {
    try {
      await navigator.clipboard.writeText(text);
      setCopyState(kind);
      setTimeout(() => setCopyState(""), 1800);
    } catch {
      // ignore
    }
  }

  return (
    <div>
      {favourited.length > 0 && (
        <div className="card" style={{ marginBottom: "1rem", padding: "0.75rem 1rem", display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
          <strong style={{ fontSize: "0.9rem" }}>{favourited.length} client pick{favourited.length === 1 ? "" : "s"}</strong>
          <span style={{ color: "var(--muted)", fontSize: "0.85rem", marginRight: "auto" }}>Export for Lightroom or hand-off:</span>
          <button
            className="btn-ghost"
            style={{ fontSize: "0.85rem", padding: "0.3rem 0.7rem" }}
            onClick={() => copyToClipboard(
              favourited.map((r) => filenameByID[r.asset_id] || r.asset_id).join("\n"),
              "names",
            )}
          >
            {copyState === "names" ? "✓ Copied" : "Copy filenames"}
          </button>
          <button
            className="btn-ghost"
            style={{ fontSize: "0.85rem", padding: "0.3rem 0.7rem" }}
            onClick={() => copyToClipboard(
              "filename,favourites\n" + favourited.map((r) => `${(filenameByID[r.asset_id] || r.asset_id).replace(/"/g, '""')},${r.favourites}`).join("\n"),
              "csv",
            )}
          >
            {copyState === "csv" ? "✓ Copied" : "Copy CSV"}
          </button>
          <a
            className="btn-primary"
            style={{ fontSize: "0.85rem", padding: "0.3rem 0.7rem", textDecoration: "none" }}
            href={api.galleryPicksZipURL(proofing.gallery_slug)}
            download
          >
            ↓ Download picks (ZIP)
          </a>
        </div>
      )}

      {favourited.length > 0 && (
        <section style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ margin: "0 0 0.75rem", fontSize: "1rem" }}>Top picks</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
            {favourited.map((r) => (
              <div key={r.asset_id} style={{ position: "relative", width: 120 }}>
                {thumbByID[r.asset_id] && (
                  <img
                    src={thumbByID[r.asset_id]}
                    alt=""
                    loading="lazy"
                    style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 6, display: "block" }}
                  />
                )}
                <span style={{
                  position: "absolute", bottom: 4, right: 4,
                  background: "rgba(0,0,0,0.65)", borderRadius: 4,
                  color: "#fff", fontSize: "0.75rem", padding: "2px 5px",
                }}>
                  ♥ {r.favourites}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {flagged.length > 0 && (
        <section style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ margin: "0 0 0.75rem", fontSize: "1rem" }}>Flagged</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
            {flagged.map((r) => (
              <div key={r.asset_id} style={{ position: "relative", width: 120 }}>
                {thumbByID[r.asset_id] && (
                  <img
                    src={thumbByID[r.asset_id]}
                    alt=""
                    loading="lazy"
                    style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 6, display: "block", border: "2px solid var(--warning)" }}
                  />
                )}
                <span style={{
                  position: "absolute", bottom: 4, right: 4,
                  background: "rgba(0,0,0,0.65)", borderRadius: 4,
                  color: "var(--warning)", fontSize: "0.75rem", padding: "2px 5px",
                }}>
                  ⚑ {r.flags}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {commented.length > 0 && (
        <section style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ margin: "0 0 0.75rem", fontSize: "1rem" }}>Comments</h3>
          {commented.map((r) =>
            r.comments.map((c) => (
              <div key={c.id} className="card" style={{ display: "flex", gap: "0.75rem", marginBottom: "0.5rem", padding: "0.75rem" }}>
                {thumbByID[r.asset_id] && (
                  <img
                    src={thumbByID[r.asset_id]}
                    alt=""
                    style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 4, flexShrink: 0 }}
                  />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: "0.875rem" }}>{c.display_name}</div>
                  <div style={{ fontSize: "0.875rem", marginTop: "0.15rem" }}>{c.body}</div>
                  <div style={{ fontSize: "0.75rem", color: "var(--muted)", marginTop: "0.25rem" }}>
                    {new Date(c.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            ))
          )}
        </section>
      )}
    </div>
  );
}

